import { useEffect, useRef, useCallback } from 'react'
import { getFirestore, doc, onSnapshot } from 'firebase/firestore'

interface UseSubscribeDineInOrderV2Props {
  orderID: number | null
  onOrderUpdate: () => Promise<void>
  onFirebaseError: (error: Error) => Promise<void>
}

const useSubscribeDineInOrderV2 = ({ orderID, onOrderUpdate, onFirebaseError }: UseSubscribeDineInOrderV2Props) => {
  const subscribeRef = useRef<VoidFunction | null>(null)
  const onOrderUpdateRef = useRef(onOrderUpdate)
  const onFirebaseErrorRef = useRef(onFirebaseError)

  useEffect(() => {
    onOrderUpdateRef.current = onOrderUpdate
  }, [onOrderUpdate])

  useEffect(() => {
    onFirebaseErrorRef.current = onFirebaseError
  }, [onFirebaseError])

  const subscribeOrder = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!orderID) {
        return
      }

      const db = getFirestore()
      const dbPath = `dineInOrder/${orderID}`
      subscribeRef.current = onSnapshot(
        doc(db, dbPath),
        async () => {
          try {
            await onOrderUpdateRef.current()
            resolve()
          } catch (error) {
            reject(error)
          }
        },
        async (error) => {
          await onFirebaseErrorRef.current(error)
          reject(error)
        }
      )
    })
  }, [orderID])

  const unSubscribeOrder = useCallback(() => {
    subscribeRef.current?.()
    subscribeRef.current = null
  }, [])

  useEffect(() => {
    return () => unSubscribeOrder()
  }, [unSubscribeOrder])

  return { subscribeOrder, unSubscribeOrder }
}

export default useSubscribeDineInOrderV2
